import { InventoryDashboardSnapshot } from '@/renderer/types/inventory';
import { Button } from '@components/common';
import { ArrowDownLeft, ArrowUpRight, TrendingUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildPerformanceChartData,
  formatCompactCurrency,
  formatCurrencyValue,
  formatQuantity,
  getMovementNarrative,
  getPurchaseNarrative,
  INVENTORY_CHART_COLORS,
  InventoryPerformanceMode,
} from './inventoryDashboard';

interface InventoryDashboardPerformanceCardProps {
  snapshot: InventoryDashboardSnapshot;
}

export function InventoryDashboardPerformanceCard({
  snapshot,
}: InventoryDashboardPerformanceCardProps) {
  const [mode, setMode] = useState<InventoryPerformanceMode>('stock_flow');

  const chartData = useMemo(
    () => buildPerformanceChartData(snapshot, mode),
    [mode, snapshot],
  );

  const totalInbound = snapshot.movements.reduce(
    (total, row) => total + row.movement_value_in,
    0,
  );
  const totalOutbound = snapshot.movements.reduce(
    (total, row) => total + row.movement_value_out,
    0,
  );
  const totalPurchases = snapshot.purchases.reduce(
    (total, row) => total + row.purchase_value,
    0,
  );
  const totalPayments = snapshot.purchases.reduce(
    (total, row) => total + row.amount_paid,
    0,
  );
  const latestSignal =
    mode === 'stock_flow'
      ? (() => {
          const leadMovementRow = [...snapshot.movements]
            .reverse()
            .find((row) => row.movement_count > 0);

          return leadMovementRow
            ? getMovementNarrative(leadMovementRow)
            : 'No activity in this range';
        })()
      : (() => {
          const leadPurchaseRow = [...snapshot.purchases]
            .reverse()
            .find((row) => row.invoice_count > 0 || row.payment_count > 0);

          return leadPurchaseRow
            ? getPurchaseNarrative(leadPurchaseRow)
            : 'No activity in this range';
        })();

  return (
    <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Inventory flow</p>
          <p className="mt-1 text-xs text-slate-500">
            Track how stock is moving in and out, then compare that against
            supplier purchasing activity.
          </p>
        </div>
        <div className="flex rounded-xl border border-gray-200 bg-slate-50 p-1">
          <Button
            size="sm"
            variant={mode === 'stock_flow' ? 'primary' : 'text'}
            className="rounded-xl"
            onClick={() => setMode('stock_flow')}
          >
            Stock flow
          </Button>
          <Button
            size="sm"
            variant={mode === 'procurement' ? 'primary' : 'text'}
            className="rounded-xl"
            onClick={() => setMode('procurement')}
          >
            Procurement
          </Button>
        </div>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl bg-pos-primary/10 px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs font-medium text-pos-primary">
            <ArrowDownLeft size={16} />
            {mode === 'stock_flow' ? 'Value in' : 'Purchases'}
          </div>
          <p className="mt-1.5 text-lg font-semibold text-slate-950">
            {formatCompactCurrency(
              mode === 'stock_flow' ? totalInbound : totalPurchases,
            )}
          </p>
        </div>
        <div className="rounded-xl bg-slate-100 px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <ArrowUpRight size={16} />
            {mode === 'stock_flow' ? 'Value out' : 'Payments'}
          </div>
          <p className="mt-1.5 text-lg font-semibold text-slate-950">
            {formatCompactCurrency(
              mode === 'stock_flow' ? totalOutbound : totalPayments,
            )}
          </p>
        </div>
        <div className="rounded-xl bg-slate-100 px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <TrendingUp size={16} />
            Activity entries
          </div>
          <p className="mt-1.5 text-lg font-semibold text-slate-950">
            {chartData
              .reduce((total, row) => total + row.activityCount, 0)
              .toLocaleString()}
          </p>
        </div>
        <div className="rounded-xl bg-slate-100 px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <TrendingUp size={16} />
            Latest signal
          </div>
          <p className="mt-1.5 text-xs font-semibold text-slate-950">
            {latestSignal}
          </p>
        </div>
      </div>

      <div className="mt-3 h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barGap={6}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
              tickFormatter={(value) =>
                mode === 'stock_flow'
                  ? formatCompactCurrency(value)
                  : formatCompactCurrency(value)
              }
            />
            <Tooltip
              formatter={(value, name) => {
                const numericValue = Number(value ?? 0);
                const seriesName = String(name ?? '');

                if (seriesName.includes('Quantity')) {
                  return [formatQuantity(numericValue), seriesName];
                }
                return [formatCurrencyValue(numericValue), seriesName];
              }}
              labelFormatter={(_, payload) => {
                const point = payload?.[0]?.payload as
                  | { metricDate?: string }
                  | undefined;
                return point?.metricDate ?? '';
              }}
            />
            <Bar
              dataKey={mode === 'stock_flow' ? 'inboundValue' : 'purchaseValue'}
              name={mode === 'stock_flow' ? 'Inbound value' : 'Purchase value'}
              fill={INVENTORY_CHART_COLORS.primary}
              radius={[8, 8, 0, 0]}
            />
            <Bar
              dataKey={mode === 'stock_flow' ? 'outboundValue' : 'paidValue'}
              name={mode === 'stock_flow' ? 'Outbound value' : 'Amount paid'}
              fill={INVENTORY_CHART_COLORS.highlight}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
